"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-20 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-mono uppercase text-[11px] tracking-[0.2em] relative group bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-2">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-white transition-all duration-[250ms] ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-8 w-48 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-8 w-52 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 mt-16">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onClose} data-index={i} className="text-foreground font-default text-3xl tracking-tight hover:text-neutral-400 transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function GalleryHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (searchOpen) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSearchOpen(false);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [searchOpen]);
  return <section data-section-id="5251" id="gallery-entrance" className="bg-background text-foreground sticky top-0 z-50 w-full">
      {}
      <div className="hidden md:block bg-background border-b border-border">
        <div className="px-12 h-6 flex items-center justify-between">
          <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground">
            EST. 2024 — LIMITED PRODUCTION
          </span>
          <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground">
            INQUIRIES — STUDIO@JOHNJOHN.COM
          </span>
        </div>
      </div>

      {}
      <div className={`w-full transition-all duration-300 ease-out ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent border-b border-transparent"}`}>
        <div className="px-6 md:px-12 h-20 flex items-center justify-between">
          {}
          <Link to="/" className="flex items-center shrink-0">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="24">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Search collection" className="text-foreground hover:bg-neutral-900 hover:text-white">
              <Search className="size-4" strokeWidth={1.25} />
            </Button>

            <CartIcon variant="ghost" size="icon" iconSize="size-4" className="text-foreground hover:bg-neutral-900 hover:text-white [&_svg]:stroke-[1.25]" />

            {}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="lg:hidden text-foreground hover:bg-neutral-900 hover:text-white">
                  <Menu className="size-5" strokeWidth={1.25} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-md p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground">
                    NAVIGATION
                  </span>
                </div>
                <MenuProvider menu_id="24">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
                <div className="mt-16 pt-8 border-t border-border">
                  <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground mb-2">
                    CONTACT
                  </p>
                  <p className="font-default text-base text-foreground">
                    studio@johnjohn.com
                  </p>
                  <p className="font-mono text-xs text-muted-foreground mt-1">
                    PRIVATE GALLERY — NEW YORK
                  </p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {}
      {searchOpen && <div className="fixed inset-0 z-[60] bg-background flex flex-col">
          <div className="px-6 md:px-12 h-20 flex items-center justify-between border-b border-border">
            <Link to="/" className="flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search" className="text-foreground hover:bg-neutral-900 hover:text-white">
              <X className="size-5" strokeWidth={1.25} />
            </Button>
          </div>
          <div className="flex-1 flex items-start justify-center pt-24 md:pt-32 px-6">
            <div className="w-full max-w-3xl">
              <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-muted-foreground mb-6">
                SEARCH THE ARCHIVE
              </p>
              <div className="relative border-b border-border pb-4">
                <Search className="absolute left-0 top-3 size-5 text-muted-foreground" strokeWidth={1.25} />
                <input autoFocus type="search" placeholder="Search for prints..." className="w-full bg-transparent pl-10 pr-4 py-2 font-default text-2xl md:text-4xl text-foreground placeholder:text-muted-foreground/60 outline-none border-0" />
              </div>
              <p className="mt-6 font-mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground">
                PRESS ESC TO CLOSE
              </p>
            </div>
          </div>
        </div>}
    </section>;
}